import { useEffect, useState } from "react";
import { api } from "../api/client";
import { useToast } from "../components/Toast";
import {
  Avatar,
  EmptyState,
  IconDownload,
  IconTrash,
  IconUsers,
  Modal,
  Spinner,
} from "../components/ui";
import { formatDate, formatDateTime } from "../utils/format";

/** RSVP list for one event, shown in a modal from the Events page. */
export default function EventRsvps({ event, onClose }) {
  const toast = useToast();
  const [rsvps, setRsvps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    api
      .get(`/events/${event.id}/rsvps`)
      .then((res) => {
        if (!cancelled) setRsvps(res.rsvps);
      })
      .catch((err) => {
        if (!cancelled) toast.error(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- toast identity is stable
  }, [event.id]);

  async function exportCsv() {
    setExporting(true);
    try {
      await api.download(`/events/${event.id}/rsvps.csv`, "rsvps.csv");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setExporting(false);
    }
  }

  async function remove(rsvp) {
    try {
      await api.del(`/events/${event.id}/rsvps/${rsvp.id}`);
      setRsvps((list) => list.filter((r) => r.id !== rsvp.id));
      toast.success(`Removed ${rsvp.name}'s RSVP.`);
    } catch (err) {
      toast.error(err.message);
    }
  }

  return (
    <Modal
      title={`RSVPs — ${event.title}`}
      subtitle={formatDateTime(event.event_date)}
      onClose={onClose}
      wide
      footer={
        <>
          <span style={{ marginRight: "auto", fontSize: 13, color: "var(--text-muted)" }}>
            {loading ? "Loading…" : `${rsvps.length} attending`}
          </span>
          <button
            type="button"
            className="btn"
            onClick={exportCsv}
            disabled={exporting || !rsvps.length}
          >
            {exporting ? <Spinner dark /> : <IconDownload />}
            Export CSV
          </button>
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Done
          </button>
        </>
      }
    >
      {loading ? (
        Array.from({ length: 4 }).map((_, i) => (
          <div className="list-item" key={i}>
            <div className="skeleton" style={{ width: 32, height: 32, borderRadius: "50%" }} />
            <div style={{ flex: 1 }}>
              <div className="skeleton" style={{ width: "40%", marginBottom: 6 }} />
              <div className="skeleton" style={{ width: "60%", height: 10 }} />
            </div>
          </div>
        ))
      ) : !rsvps.length ? (
        <EmptyState
          icon={IconUsers}
          title="No RSVPs yet"
          message="When someone reserves a spot from the public site, they'll show up here."
        />
      ) : (
        rsvps.map((r) => (
          <div className="list-item" key={r.id}>
            <Avatar name={r.name} id={r.id} />
            <div className="list-item-body">
              <div className="list-item-title">{r.name}</div>
              <div className="list-item-meta">
                {[r.designation, r.company].filter(Boolean).join(" · ")}
              </div>
              <div className="list-item-meta" style={{ color: "var(--text-faint)" }}>
                {r.email}
              </div>
            </div>
            <span className="list-item-time">{formatDate(r.created_at)}</span>
            <button
              type="button"
              className="btn btn-sm btn-icon btn-ghost"
              onClick={() => remove(r)}
              title="Remove this RSVP"
            >
              <IconTrash style={{ color: "var(--red)" }} />
            </button>
          </div>
        ))
      )}
    </Modal>
  );
}
